import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useT } from "../i18n/LanguageContext.jsx";
import { LOCALES, secondaryRiskLabel } from "../i18n/strings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FILTERS = [
  { key: "all", value: undefined, testid: "filter-all" },
  { key: "pending", value: "pending", testid: "filter-pending" },
  { key: "yes", value: "yes", testid: "filter-yes" },
  { key: "no", value: "no", testid: "filter-no" },
];

function formatWhen(iso, lang) {
  if (!iso) return "—";
  const d = new Date(iso);
  return new Intl.DateTimeFormat(LOCALES[lang] || LOCALES.en, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

function ValidationBadge({ value, t }) {
  if (value === true) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 text-[11px] font-mono border border-emerald-300 bg-emerald-50 text-emerald-800">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> {t("tx.validation.yes")}
      </span>
    );
  }
  if (value === false) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 text-[11px] font-mono border border-red-300 bg-red-50 text-red-800">
        <span className="w-1.5 h-1.5 rounded-full bg-red-500" /> {t("tx.validation.no")}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 text-[11px] font-mono border border-slate-300 bg-white text-slate-600">
      <span className="w-1.5 h-1.5 rounded-full bg-slate-400" /> {t("tx.validation.pending")}
    </span>
  );
}

export default function TransactionsPage() {
  const { t, lang } = useT();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState(undefined);
  const [caller, setCaller] = useState("");
  const [txIdQuery, setTxIdQuery] = useState("");
  const [refQuery, setRefQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [error, setError] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const params = { limit: 100 };
        if (filter) params.validation = filter;
        if (caller.trim()) params.caller = caller.trim();
        if (txIdQuery.trim()) params.transaction_id = txIdQuery.trim();
        if (refQuery.trim()) params.patient_reference = refQuery.trim();
        const [tx, st] = await Promise.all([
          axios.get(`${API}/transactions`, { params }),
          axios.get(`${API}/transactions/stats`),
        ]);
        if (cancelled) return;
        setItems(tx.data.items || []);
        setTotal(tx.data.total || 0);
        setStats(st.data);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || String(e.message || e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [filter, caller, txIdQuery, refQuery, refreshTick]);

  const refresh = () => setRefreshTick((n) => n + 1);

  const toggle = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const copyId = async (id) => {
    try {
      await navigator.clipboard.writeText(id);
      setCopiedId(id);
      setTimeout(() => setCopiedId((cur) => (cur === id ? null : cur)), 1200);
    } catch {
      // fallback: ignore - browser/clipboard may be blocked in some preview envs
    }
  };

  const onValidate = async (txId, validation, notes) => {
    try {
      const r = await axios.put(`${API}/transactions/${txId}/validation`, { validation, notes });
      setItems((prev) => prev.map((it) => (it.id === txId ? r.data : it)));
      const st = await axios.get(`${API}/transactions/stats`);
      setStats(st.data);
    } catch (e) {
      setError(e.response?.data?.detail || String(e.message || e));
    }
  };

  const onDelete = async (txId) => {
    if (!window.confirm(t("tx.delete.confirm"))) return;
    try {
      await axios.delete(`${API}/transactions/${txId}`);
      // Drop locally so the table updates immediately without a roundtrip
      // for the whole list; refresh stats so the dashboard tiles stay in sync.
      setItems((prev) => prev.filter((it) => it.id !== txId));
      setTotal((n) => Math.max(0, n - 1));
      setExpanded((prev) => {
        const next = { ...prev };
        delete next[txId];
        return next;
      });
      try {
        const st = await axios.get(`${API}/transactions/stats`);
        setStats(st.data);
      } catch {
        // stats refresh is best-effort; the row removal above is what the
        // operator clicked for, so don't surface a secondary failure.
      }
    } catch (e) {
      setError(e.response?.data?.detail || String(e.message || e));
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    setError(null);
    try {
      const params = {};
      if (filter) params.validation = filter;
      if (caller.trim()) params.caller = caller.trim();
      if (txIdQuery.trim()) params.transaction_id = txIdQuery.trim();
      if (refQuery.trim()) params.patient_reference = refQuery.trim();
      const r = await axios.get(`${API}/transactions/export.csv`, {
        params,
        responseType: "blob",
      });
      // Prefer the filename from Content-Disposition; fall back to a
      // reasonable default. `responseType: "blob"` means we must decode
      // the header manually.
      const cd = r.headers?.["content-disposition"] || "";
      const match = /filename\s*=\s*"?([^"]+)"?/i.exec(cd);
      const filename = match ? match[1] : "sepsis-transactions.csv";
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Small delay before revoking so Firefox has a chance to consume it.
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (e) {
      setError(e.response?.data?.detail || String(e.message || e));
    } finally {
      setExporting(false);
    }
  };

  const statCards = useMemo(
    () => [
      { key: "total", label: t("tx.stats.total"), value: stats?.total ?? 0, accent: "slate" },
      { key: "pending", label: t("tx.stats.pending"), value: stats?.pending ?? 0, accent: "amber" },
      { key: "yes", label: t("tx.stats.yes"), value: stats?.validated_yes ?? 0, accent: "emerald" },
      { key: "no", label: t("tx.stats.no"), value: stats?.validated_no ?? 0, accent: "red" },
    ],
    [stats, t]
  );

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-10" data-testid="transactions-page">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-8">
        <div>
          <div className="data-label mb-2">{t("tx.kicker")}</div>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
            {t("tx.title")}
          </h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">{t("tx.description")}</p>
        </div>
        <button
          onClick={refresh}
          data-testid="btn-refresh-tx"
          className="px-3 py-2 text-xs font-mono border border-slate-300 hover:bg-slate-50"
        >
          {t("tx.refresh")}
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-slate-200 border border-slate-200 mb-6" data-testid="tx-stats">
        {statCards.map((s) => (
          <div key={s.key} className="bg-white p-4">
            <div className="data-label">{s.label}</div>
            <div className="font-mono text-3xl tabular-nums text-slate-900 mt-1" data-testid={`stat-${s.key}`}>
              {s.value}
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.value)}
            data-testid={f.testid}
            className={[
              "px-3 py-1.5 text-xs font-mono border transition-colors",
              filter === f.value
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50",
            ].join(" ")}
          >
            {t(`tx.filter.${f.key}`)}
          </button>
        ))}
        <div className="relative">
          <input
            type="text"
            value={txIdQuery}
            onChange={(e) => setTxIdQuery(e.target.value)}
            placeholder={t("tx.filter.id")}
            data-testid="filter-txid"
            className="font-mono text-xs px-3 py-1.5 pr-7 border border-slate-300 focus:border-slate-900 outline-none min-w-[300px]"
          />
          {txIdQuery && (
            <button
              onClick={() => setTxIdQuery("")}
              data-testid="filter-txid-clear"
              aria-label={t("tx.filter.clear")}
              className="absolute right-1 top-1/2 -translate-y-1/2 px-1.5 text-slate-400 hover:text-slate-900 text-sm font-mono leading-none"
              type="button"
            >
              ×
            </button>
          )}
        </div>
        <div className="relative">
          <input
            type="text"
            value={refQuery}
            onChange={(e) => setRefQuery(e.target.value)}
            placeholder={t("tx.filter.ref")}
            data-testid="filter-patient-ref"
            className="font-mono text-xs px-3 py-1.5 pr-7 border border-slate-300 focus:border-slate-900 outline-none min-w-[260px]"
          />
          {refQuery && (
            <button
              onClick={() => setRefQuery("")}
              data-testid="filter-patient-ref-clear"
              aria-label={t("tx.filter.clear")}
              className="absolute right-1 top-1/2 -translate-y-1/2 px-1.5 text-slate-400 hover:text-slate-900 text-sm font-mono leading-none"
              type="button"
            >
              ×
            </button>
          )}
        </div>
        <input
          type="email"
          value={caller}
          onChange={(e) => setCaller(e.target.value)}
          placeholder={t("tx.filter.caller")}
          data-testid="filter-caller"
          className="font-mono text-xs px-3 py-1.5 border border-slate-300 focus:border-slate-900 outline-none min-w-[260px]"
        />
        <button
          type="button"
          onClick={exportCsv}
          disabled={exporting}
          title={t("tx.export.csv.help")}
          data-testid="btn-export-csv"
          className="ml-auto px-3 py-1.5 text-xs font-mono border border-slate-900 bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
        >
          <span aria-hidden="true" className="text-[13px] leading-none">↓</span>
          {exporting ? t("tx.export.csv.exporting") : t("tx.export.csv")}
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 border border-red-300 bg-red-50 text-red-800 text-sm" data-testid="tx-error">
          {error}
        </div>
      )}

      {loading ? (
        <div className="data-label" data-testid="tx-loading">{t("tx.loading")}</div>
      ) : items.length === 0 ? (
        <div className="border border-slate-200 p-10 text-center clinical-grid" data-testid="tx-empty">
          <div className="text-sm text-slate-500 max-w-md mx-auto">
            {(txIdQuery.trim() || refQuery.trim()) ? t("tx.empty.search") : t("tx.empty")}
          </div>
        </div>
      ) : (
        <div className="border border-slate-200 overflow-x-auto" data-testid="tx-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-left">
              <tr>
                <th className="px-4 py-3 data-label">{t("tx.col.id")}</th>
                <th className="px-4 py-3 data-label">{t("tx.col.ref")}</th>
                <th className="px-4 py-3 data-label whitespace-nowrap">{t("tx.col.when")}</th>
                <th className="px-4 py-3 data-label">{t("tx.col.caller")}</th>
                <th className="px-4 py-3 data-label text-right">{t("tx.col.prob")}</th>
                <th className="px-4 py-3 data-label">{t("tx.col.class")}</th>
                <th className="px-4 py-3 data-label">{t("tx.col.validation")}</th>
                <th className="px-4 py-3 data-label"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => {
                const p = it.output?.combined?.probability_sepsis ?? 0;
                const rcEn = it.output?.combined?.risk_class_en || "—";
                const isOpen = !!expanded[it.id];
                const shortId = (it.id || "").split("-")[0];
                return (
                  <React.Fragment key={it.id}>
                    <tr className="row-hover border-b border-slate-100" data-testid={`tx-row-${it.id}`}>
                      <td className="px-4 py-2 font-mono text-[12px] text-slate-900 whitespace-nowrap">
                        <div className="flex items-center gap-1.5">
                          <span title={it.id} data-testid={`tx-id-${it.id}`}>{shortId}</span>
                          <button
                            type="button"
                            onClick={() => copyId(it.id)}
                            data-testid={`btn-copy-id-${it.id}`}
                            title={it.id}
                            aria-label={t("tx.copyId")}
                            className="text-[10px] px-1.5 py-0.5 border border-slate-200 hover:bg-slate-50 text-slate-500"
                          >
                            {copiedId === it.id ? "✓" : "⧉"}
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-2 font-mono text-[12px] text-slate-900 max-w-[180px] truncate" title={it.patient_reference || ""} data-testid={`tx-ref-${it.id}`}>
                        {it.patient_reference || <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-4 py-2 font-mono text-[12px] text-slate-700 whitespace-nowrap">
                        {formatWhen(it.timestamp, lang)}
                      </td>
                      <td className="px-4 py-2 font-mono text-[12px] text-slate-900">{it.caller_email}</td>
                      <td className="px-4 py-2 text-right font-mono tabular-nums text-slate-900">
                        {Number(p).toFixed(4)}
                      </td>
                      <td className="px-4 py-2 text-slate-700 text-xs">{rcEn}</td>
                      <td className="px-4 py-2"><ValidationBadge value={it.validation} t={t} /></td>
                      <td className="px-4 py-2 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            onClick={() => toggle(it.id)}
                            data-testid={`btn-toggle-${it.id}`}
                            className="px-2 py-1 text-[11px] font-mono border border-slate-300 hover:bg-slate-50"
                          >
                            {isOpen ? t("tx.detail.collapse") : t("tx.detail.expand")}
                          </button>
                          <button
                            onClick={() => onDelete(it.id)}
                            data-testid={`btn-delete-${it.id}`}
                            aria-label={t("tx.delete")}
                            title={t("tx.delete")}
                            className="px-2 py-1 text-[11px] font-mono border border-red-300 text-red-700 hover:bg-red-50"
                          >
                            {t("tx.delete")}
                          </button>
                        </div>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-slate-50/60 border-b border-slate-200">
                        <td colSpan={8} className="px-6 py-5">
                          <TransactionDetail
                            key={`${it.id}-${it.validation}-${it.notes ?? ""}`}
                            tx={it}
                            t={t}
                            onValidate={onValidate}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          <div className="px-4 py-2 border-t border-slate-200 bg-slate-50 text-[11px] font-mono text-slate-500" data-testid="tx-pagination">
            {items.length} / {total}
          </div>
        </div>
      )}
    </div>
  );
}

function TransactionDetail({ tx, t, onValidate }) {
  const { lang } = useT();
  const [notes, setNotes] = useState(tx.notes || "");
  const [saving, setSaving] = useState(false);

  const handle = async (validation) => {
    setSaving(true);
    try {
      await onValidate(tx.id, validation, notes);
    } finally {
      setSaving(false);
    }
  };

  const combined = tx.output?.combined || {};
  const patient = tx.input?.patient || {};
  const path = tx.output?.decision_tree?.details?.path_conditions || [];
  const node = tx.output?.decision_tree?.details?.matched_node;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="border border-slate-200 bg-white">
        <div className="px-4 py-2 bg-slate-100 data-label border-b border-slate-200">
          {t("tx.detail.input")}
        </div>
        <div className="p-3 grid grid-cols-2 gap-x-3 gap-y-1 text-[12px] font-mono">
          {Object.entries(patient).map(([k, v]) => (
            <div key={k} className="flex items-center justify-between border-b border-slate-100 py-0.5">
              <span className="text-slate-500">{k}</span>
              <span className="text-slate-900 tabular-nums">{Number(v).toString()}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="border border-slate-200 bg-white">
        <div className="px-4 py-2 bg-slate-100 data-label border-b border-slate-200">
          {t("tx.detail.output")}
        </div>
        <div className="p-4 space-y-3">
          <div className="flex items-baseline justify-between gap-3">
            <span className="data-label">{t("tx.col.id")}</span>
            <span className="font-mono text-[11px] text-slate-700 break-all text-right" data-testid={`tx-detail-id-${tx.id}`}>
              {tx.id}
            </span>
          </div>
          {tx.patient_reference && (
            <div className="flex items-baseline justify-between gap-3" data-testid={`tx-detail-ref-block-${tx.id}`}>
              <span className="data-label">{t("tx.col.ref")}</span>
              <span className="font-mono text-[11px] text-slate-900 break-all text-right" data-testid={`tx-detail-ref-${tx.id}`}>
                {tx.patient_reference}
              </span>
            </div>
          )}
          <div className="flex items-baseline justify-between">
            <span className="data-label">P(SEPSIS)</span>
            <span className="font-mono tabular-nums text-2xl text-slate-900">
              {Number(combined.probability_sepsis || 0).toFixed(4)}
            </span>
          </div>
          <div className="flex items-baseline justify-between">
            <span className="data-label">{t("tx.col.class")}</span>
            <span className="text-sm text-slate-900">
              {combined.risk_class_en} · <span className="text-slate-500">{secondaryRiskLabel(combined, lang)}</span>
            </span>
          </div>
          {node && (
            <div className="pt-3 border-t border-slate-200">
              <div className="data-label mb-2">{t("tx.detail.path")} · node {node}</div>
              <ul className="space-y-1 font-mono text-[11px] text-slate-700">
                {path.map((c, i) => (
                  <li key={i}>
                    <span className="text-slate-400 mr-2">{String(i + 1).padStart(2, "0")}</span>
                    {c.variable} {c.operator} {c.threshold}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      <div className="border border-slate-200 bg-white">
        <div className="px-4 py-2 bg-slate-100 data-label border-b border-slate-200">
          {t("tx.col.validation")}
        </div>
        <div className="p-4 space-y-3">
          <div className="flex items-center gap-2 mb-2">
            <ValidationBadge value={tx.validation} t={t} />
            {tx.validated_by && (
              <span className="text-[11px] font-mono text-slate-500">
                {t("tx.validation.by")} {tx.validated_by}
              </span>
            )}
          </div>
          <div>
            <label className="data-label block mb-1">{t("tx.validation.notes")}</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t("tx.validation.notesPh")}
              data-testid={`notes-${tx.id}`}
              className="w-full text-xs font-mono px-3 py-2 border border-slate-300 focus:border-slate-900 outline-none min-h-[80px]"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => handle(true)}
              disabled={saving}
              data-testid={`btn-yes-${tx.id}`}
              className="flex-1 min-w-[110px] px-3 py-2 text-xs font-medium border border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              {t("tx.validation.markYes")}
            </button>
            <button
              onClick={() => handle(false)}
              disabled={saving}
              data-testid={`btn-no-${tx.id}`}
              className="flex-1 min-w-[110px] px-3 py-2 text-xs font-medium border border-red-600 bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
            >
              {t("tx.validation.markNo")}
            </button>
            <button
              onClick={() => handle(null)}
              disabled={saving}
              data-testid={`btn-clear-${tx.id}`}
              className="px-3 py-2 text-xs font-mono border border-slate-300 hover:bg-slate-50 disabled:opacity-50"
            >
              {t("tx.validation.clear")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
